import {
  postContactoCtasBancarias,
  putContactoCtasBancarias,
} from "api/Contacto.service"
import { EntidadBancariaDtoProps } from "api/DTOs/EntidadBancariaDto"
import { TipoRemesaType } from "api/Types/TipoRemesa"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import notification from "common/utils/notification"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CreateEditEntidadModalProps } from "../.."
import {
  adapterPostCuentaBancariaContacto,
  adapterPutCuentaBancariaContacto,
} from "./constant"

interface useCuentaBancariasFormProps {
  id: string
  createOrEditModal?: CreateEditEntidadModalProps
  contactosCuentaBancariaByIdRefetch: () => void
  onHide: () => void
}

export interface MandatoFormValues {
  fecha?: Date | null
  referencia?: string | null
  tipoRemesa?: TipoRemesaType | null
}

export interface CuentaBancariaFormValues {
  id?: string
  bic?: string | null
  entidadBancariaId?: string | null
  iban?: string | null
  canEditBic?: boolean
  mandato: MandatoFormValues
  principal: boolean
  prefilter?: Partial<EntidadBancariaDtoProps>
}

const INITIAL_CUENTA_BANCARIA_FORM_VALUES: CuentaBancariaFormValues = {
  id: undefined,
  bic: null,
  entidadBancariaId: null,
  iban: null,
  canEditBic: false,
  mandato: {
    fecha: null,
    referencia: null,
    tipoRemesa: null,
  },
  principal: false,
  prefilter: {
    id: undefined,
    nombre: undefined,
    bic: undefined,
  },
}

export const useCuentaBancariasForm = ({
  id,
  createOrEditModal,
  contactosCuentaBancariaByIdRefetch,
  onHide,
}: useCuentaBancariasFormProps) => {
  const { t } = useTranslation()
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<CuentaBancariaFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: INITIAL_CUENTA_BANCARIA_FORM_VALUES,
    values: createOrEditModal?.isEdit
      ? {
          id: createOrEditModal.isEdit.id,
          bic: createOrEditModal.isEdit.bic,
          entidadBancariaId: createOrEditModal.isEdit.entidadBancariaId,
          iban: createOrEditModal.isEdit.iban,
          canEditBic: createOrEditModal.isEdit.canEditBic ?? false,
          mandato: {
            fecha: createOrEditModal.isEdit.mandato?.fecha
              ? new Date(createOrEditModal.isEdit.mandato.fecha)
              : undefined,
            referencia: createOrEditModal.isEdit.mandato?.referencia,
            tipoRemesa: createOrEditModal.isEdit.mandato?.tipoRemesa,
          },
          principal: createOrEditModal.isEdit.principal ?? false,
          prefilter: {
            id: createOrEditModal.isEdit.entidadBancariaId ?? undefined,
            nombre: createOrEditModal.isEdit.entidadBancariaNombre ?? undefined,
            bic: createOrEditModal.isEdit.bic ?? undefined,
          },
        }
      : INITIAL_CUENTA_BANCARIA_FORM_VALUES,
  })

  const resetForm = () => reset(INITIAL_CUENTA_BANCARIA_FORM_VALUES)
  const onSubmit: SubmitHandler<CuentaBancariaFormValues> = async (
    data: CuentaBancariaFormValues,
    event?: React.BaseSyntheticEvent
  ) => {
    event?.preventDefault()
    event?.stopPropagation()
    if (id) {
      try {
        if (!createOrEditModal?.isEdit) {
          const { status } = await postContactoCtasBancarias({
            id,
            requestBody: adapterPostCuentaBancariaContacto(data),
          })

          if (status === STATUSCORRECT) {
            contactosCuentaBancariaByIdRefetch()
            notification.success({
              body: `${t("contactos.cuentasBancarias.createSuccess")}`,
            })
          }
        } else {
          if (!data.id) return
          const { status } = await putContactoCtasBancarias({
            id,
            idCuentaBancaria: data.id,
            requestBody: adapterPutCuentaBancariaContacto(data),
          })

          if (status === STATUSCORRECT) {
            contactosCuentaBancariaByIdRefetch()
            notification.success({
              body: `${t("contactos.cuentasBancarias.editSuccess")}`,
            })
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error al crear la cuenta bancaria", error)
      } finally {
        onHide()
        resetForm()
      }
    }
  }
  return {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    onSubmit,
    errors,
    resetForm,
  }
}
